import React, { useEffect, useMemo, useState } from 'react'
import Icon from '../../../components/AppIcon'
import { fetchGoldTransactions, inLastDays, isCancelledTx, usd, grams } from '../lib/goldTx'
import { useMetalPrices } from '../hooks/useMetalPrices'
import RissReportingPanel from './RissReportingPanel'

const WINDOWS = [30, 60, 90]

/**
 * AU profit report: is the store making money on the gold bought in the last
 * 30/60/90 days? For each window we compare what was PAID for the gold
 * (payout, already net of deságio) against what that pure gold is WORTH at
 * today's live price.
 */
function windowReport(txs, days, goldPriceNow) {
  const rows = txs.filter((t) => inLastDays(t, days) && t.goldPureGrams > 0)
  const goldGrams = rows.reduce((s, t) => s + t.goldGrams, 0)
  const pureAu = rows.reduce((s, t) => s + t.goldPureGrams, 0)
  const paid = rows.reduce((s, t) => s + t.goldPaid, 0)
  const desagio = rows.reduce((s, t) => s + (t.desagioAmount || 0), 0)
  const marketValue = goldPriceNow ? pureAu * goldPriceNow : null
  const profit = marketValue != null ? marketValue - paid : null
  const marginPct = profit != null && paid > 0 ? (profit / paid) * 100 : null
  const avgPaidPerPureGram = pureAu > 0 ? paid / pureAu : null
  return { days, count: rows.length, goldGrams, pureAu, paid, desagio, marketValue, profit, marginPct, avgPaidPerPureGram }
}

function ReportCard({ r, goldPriceNow }) {
  const profitable = r.profit != null && r.profit >= 0
  return (
    <div className={`rounded-2xl border p-5 ${r.profit == null ? 'border-border bg-card' : profitable ? 'border-success/40 bg-success/5' : 'border-destructive/40 bg-destructive/5'}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Last {r.days} days</p>
        {r.profit != null && (
          <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${profitable ? 'bg-success/15 text-success' : 'bg-destructive/15 text-destructive'}`}>
            <Icon name={profitable ? 'TrendingUp' : 'TrendingDown'} size={14} />
            {profitable ? 'PROFITABLE' : 'UNDERWATER'}
          </span>
        )}
      </div>

      {r.count === 0 ? (
        <p className="text-sm text-muted-foreground">No gold purchases in this window.</p>
      ) : (
        <>
          <p className={`text-3xl font-bold ${r.profit == null ? 'text-foreground' : profitable ? 'text-success' : 'text-destructive'}`}>
            {r.profit != null ? `${r.profit >= 0 ? '+' : ''}${usd(r.profit)}` : '—'}
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            {r.marginPct != null ? `${r.marginPct >= 0 ? '+' : ''}${r.marginPct.toFixed(1)}% over what was paid` : 'Live price unavailable'}
          </p>

          <div className="space-y-1.5 text-sm border-t border-border/60 pt-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Purchases</span>
              <span className="text-foreground font-medium">{r.count}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Gold bought</span>
              <span className="text-foreground font-medium">{grams(r.goldGrams)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Pure AU (24K equiv.)</span>
              <span className="text-foreground font-medium">{grams(r.pureAu)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Paid (net of deságio)</span>
              <span className="text-foreground font-semibold">{usd(r.paid)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Avg paid per pure gram</span>
              <span className="text-foreground font-medium">{r.avgPaidPerPureGram != null ? `${usd(r.avgPaidPerPureGram)}/g` : '—'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Deságio captured</span>
              <span className="text-success font-medium">{usd(r.desagio)}</span>
            </div>
            <div className="flex justify-between border-t border-border/60 pt-1.5 mt-1.5">
              <span className="text-muted-foreground">Worth today {goldPriceNow ? `(@ ${usd(goldPriceNow)}/g)` : ''}</span>
              <span className="text-foreground font-semibold">{r.marketValue != null ? usd(r.marketValue) : '—'}</span>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default function ExchangeReports() {
  const [txs, setTxs] = useState(null)
  const [error, setError] = useState('')
  const { prices, loading: pricesLoading } = useMetalPrices()
  const goldPriceNow = prices?.gold || null

  useEffect(() => {
    let alive = true
    fetchGoldTransactions()
      // Cancelled sales are void — profit/AU reports must not count them.
      .then((rows) => { if (alive) setTxs(rows.filter((t) => !isCancelledTx(t))) })
      .catch((e) => { if (alive) setError(e?.message || 'Failed to load transactions') })
    return () => { alive = false }
  }, [])

  const reports = useMemo(() => {
    if (!txs) return null
    return WINDOWS.map((d) => windowReport(txs, d, goldPriceNow))
  }, [txs, goldPriceNow])

  if (error) {
    return <div className="space-y-4"><RissReportingPanel /><div className="bg-card rounded-2xl border border-border p-6 text-sm text-destructive">{error}</div></div>
  }
  if (!txs || pricesLoading) {
    return <div className="space-y-4"><RissReportingPanel /><div className="bg-card rounded-2xl border border-border p-8 text-center text-muted-foreground text-sm">Loading profit report…</div></div>
  }

  return (
    <div className="space-y-4">
      <RissReportingPanel />
      {!goldPriceNow && (
        <div className="rounded-xl border border-warning/40 bg-warning/10 p-3 text-sm text-warning flex items-center gap-2">
          <Icon name="AlertTriangle" size={16} />
          Live gold price unavailable — market values can’t be computed right now.
        </div>
      )}
      {prices?.stale && (
        <div className="rounded-xl border border-warning/40 bg-warning/10 p-3 text-sm text-warning flex items-center gap-2">
          <Icon name="Clock" size={16} />
          Using a stale gold price ({prices.ageMinutes != null ? `${prices.ageMinutes} min old` : 'age unknown'}) — refresh prices on the Buy screen for an up-to-date report.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {reports.map((r) => <ReportCard key={r.days} r={r} goldPriceNow={goldPriceNow} />)}
      </div>

      <div className="rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground space-y-1.5">
        <p className="font-medium text-foreground flex items-center gap-2"><Icon name="Info" size={15} /> How this is calculated</p>
        <p>
          <span className="text-foreground font-medium">Paid</span> is what left the register for gold items (already net of the deságio).{' '}
          <span className="text-foreground font-medium">Worth today</span> multiplies the pure 24K grams bought by the live gold price.{' '}
          The difference is the unrealized result if that gold were sold at spot today — the deságio (jewelry {''}30% / coin-bar 20%) is the margin built in at the moment of purchase, and gold price movement since then adds or removes from it.
        </p>
        <p className="text-xs">Silver is excluded from this report; it covers AU only.</p>
      </div>
    </div>
  )
}
